import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search, Download, FileSpreadsheet, Loader2, Trash2 } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { toast } from 'sonner';
import { maintenanceService } from '@/services/maintenance-service';
import type { MaintenanceTransaction } from '@/types/maintenance';
import { MAINTENANCE_TYPE_LABELS } from '@/types/maintenance';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function MaintenanceLog() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<MaintenanceTransaction[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await maintenanceService.getAll({
        pageNumber: currentPage,
        pageSize,
        sortBy: 'maintenanceDate',
        sortDirection: 'desc',
      });
      setTransactions(response.items);
      setTotalCount(response.totalCount);
    } catch {
      toast.error('Failed to load maintenance transactions');
    }finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await maintenanceService.delete(deleteId);
      toast.success('Maintenance record deleted successfully');
      setDeleteId(null);
      loadData();
    } catch {
      toast.error('Failed to delete maintenance record');
    }
  };

  const handleExportCSV = () => {
    const csvData = transactions.map(t => ({
      Reference: t.reference,
      'Asset Code': t.assetCode,
      'Asset Name': t.assetName,
      'Maintenance Date': formatDate(t.maintenanceDate),
      Type: MAINTENANCE_TYPE_LABELS[t.maintenanceType],
      Supplier: t.supplierName || '',
      Net: t.net.toFixed(2),
      Tax: t.tax.toFixed(2),
      Gross: t.gross.toFixed(2),
      'Warranty Work': t.warrantyWork ? 'Yes' : 'No',
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maintenance-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.reference.toLowerCase().includes(query) ||
      t.assetCode.toLowerCase().includes(query) ||
      t.assetName.toLowerCase().includes(query) ||
      t.supplierName?.toLowerCase().includes(query)
    );
  });

  return (
    <>
      <PageWithRightPanel>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold">Maintenance Log</h1>
              <p className="text-muted-foreground mt-1">
                View and manage all maintenance records
              </p>
            </div>
          </div>

          {/* Search and Actions */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference, asset, or supplier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleExportCSV} disabled={transactions.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>

          {/* Table */}
          <div className="flex-1 border rounded-lg overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
            {!loading && filteredTransactions.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 mb-4 opacity-50" />
                <p>No maintenance records found</p>
              </div>
            )}
            {!loading && filteredTransactions.length > 0 && (
              <div className="overflow-auto h-full">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Asset Code</TableHead>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead>Warranty</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-medium">{transaction.reference}</TableCell>
                        <TableCell>{transaction.assetCode}</TableCell>
                        <TableCell>{transaction.assetName}</TableCell>
                        <TableCell>{formatDate(transaction.maintenanceDate)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {MAINTENANCE_TYPE_LABELS[transaction.maintenanceType]}
                          </Badge>
                        </TableCell>
                        <TableCell>{transaction.supplierName || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(transaction.net)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(transaction.tax)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(transaction.gross)}
                        </TableCell>
                        <TableCell>
                          {transaction.warrantyWork && (
                            <Badge variant="secondary">Warranty</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(transaction.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalCount > pageSize && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage * pageSize >= totalCount}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </PageWithRightPanel>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Maintenance Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this maintenance record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
